import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toggleTheme } from "../authslice";
import { Container, Button } from "react-bootstrap";
import { Link } from "react-router-dom";
import { RootState } from "../store";
import { useTranslation } from "react-i18next";
import { ENABLED_LANGUAGES, Language } from "../../i18n/languages";

const Header: React.FC = () => {
  const [scrolling, setScrolling] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const theme = useSelector((state: RootState) => state.theme.theme);
  const { i18n } = useTranslation();

  const currentLang =
    ENABLED_LANGUAGES.find((l) => l.code === i18n.language) ||
    ENABLED_LANGUAGES[0];

  useEffect(() => {
    document.body.classList.remove("light", "dark");
    document.body.classList.add(theme);
    return () => { document.body.classList.remove("light", "dark"); };
  }, [theme]);

  useEffect(() => {
    document.documentElement.dir = currentLang.dir;
    document.documentElement.lang = currentLang.code;
  }, [currentLang]);

  useEffect(() => {
    const handleScroll = () => setScrolling(window.scrollY > 0);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLanguageChange = (lang: Language) => {
    i18n.changeLanguage(lang.code);
    setLangOpen(false);
  };

  return (
    <nav
      className={`navbar sticky-top ${
        theme === "light"
          ? scrolling ? "bg-white shadow-sm" : "bg-transparent"
          : "bg-dark shadow-sm"
      }`}
    >
      <Container fluid className="px-3 px-md-5">
        <Link className="navbar-brand" to={"/"}>
          <img
            src={theme === "dark" ? "/ARMF_Dark_logo.png" : "/ARMF_Light_logo.png"}
            width={100}
            alt="ARM Financial"
          />
        </Link>
        <div className="d-flex align-items-center me-3 me-md-5 mt-2 mt-md-3 gap-1" style={{ transform: "translateY(-4px)" }}>
          {ENABLED_LANGUAGES.length > 1 ? (
            <div ref={langRef} style={{ position: "relative" }}>
              <button
                className="btn d-flex align-items-center justify-content-center border-0"
                style={{ 
                  fontSize: "0.85rem", 
                  fontWeight: 600,
                  padding: "6px 10px", 
                  background: "transparent",
                  color: theme === "dark" ? "#e2e8f0" : "#475569",
                  transition: "color 0.2s ease"
                }}
                onMouseEnter={(e) => { e.currentTarget.style.color = theme === 'dark' ? '#fff' : '#0f172a'; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = theme === 'dark' ? '#e2e8f0' : '#475569'; }}
                onClick={() => setLangOpen((o) => !o)}
              >
                <i className="fa-solid fa-globe me-2 opacity-75" />
                {currentLang.label}
              </button>
              {langOpen && (
                <div
                  style={{
                    position: "absolute",
                    top: "calc(100% + 12px)",
                    right: 0,
                    minWidth: 140,
                    background: theme === "dark" ? "rgba(30, 52, 78, 0.85)" : "rgba(255, 255, 255, 0.85)",
                    backdropFilter: "blur(12px)",
                    border: `1px solid ${theme === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)"}`,
                    borderRadius: 12,
                    boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
                    zIndex: 9999,
                    overflow: "hidden",
                  }}
                >
                  {ENABLED_LANGUAGES.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => handleLanguageChange(lang)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        width: "100%",
                        padding: "10px 16px",
                        border: "none",
                        background: currentLang.code === lang.code ? (theme === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.04)") : "transparent",
                        color: theme === "dark" ? "#f8fafc" : "#1e293b",
                        cursor: "pointer",
                        fontSize: "0.85rem",
                        textAlign: "left",
                        transition: "background 0.2s"
                      }}
                      onMouseEnter={(e) => { if(currentLang.code !== lang.code) e.currentTarget.style.background = theme === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)' }}
                      onMouseLeave={(e) => { if(currentLang.code !== lang.code) e.currentTarget.style.background = 'transparent' }}
                    >
                      <span style={{ fontWeight: 700, minWidth: 22 }}>{lang.label}</span>
                      <span style={{ opacity: 0.6 }}>{lang.nativeName}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <button
              className="btn d-flex align-items-center justify-content-center border-0"
              style={{ 
                fontSize: "0.85rem", 
                fontWeight: 600,
                padding: "6px 10px", 
                background: "transparent",
                color: theme === "dark" ? "#e2e8f0" : "#475569"
              }}
            >
              <i className="fa-solid fa-globe me-2 opacity-75" />
              {currentLang.label}
            </button>
          )}

          <div style={{ width: 1, height: 16, background: theme === "dark" ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.15)", margin: "0 6px" }} />

          <button
            className="btn d-flex align-items-center justify-content-center border-0"
            style={{ 
              width: 36, 
              height: 36, 
              padding: 0, 
              background: "transparent",
              color: theme === "dark" ? "#fcd34d" : "#6366f1",
              boxShadow: "none",
              transition: "transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), color 0.2s"
            }}
            onMouseEnter={(e) => { 
              e.currentTarget.style.transform = 'rotate(15deg) scale(1.15)'; 
              e.currentTarget.style.color = theme === 'dark' ? '#fde047' : '#4f46e5';
            }}
            onMouseLeave={(e) => { 
              e.currentTarget.style.transform = 'rotate(0deg) scale(1)'; 
              e.currentTarget.style.color = theme === 'dark' ? '#fcd34d' : '#6366f1';
            }}
            onClick={() => dispatch(toggleTheme())}
            title={theme === "dark" ? "Switch to Light Mode" : "Switch to Dark Mode"}
          >
            <i className={`fa-solid ${theme === "dark" ? "fa-sun" : "fa-moon"} fs-5`} />
          </button>
        </div>
      </Container>
    </nav>
  );
};

export default Header;
